
import axios from "axios";
import { useContext, useEffect, useRef, useState } from "react";
import { useSpring } from "react-spring";

import styled from 'styled-components';
import { AppContext } from '~/contexts/AppContext';
import { GenderData } from '~/App';

import SmileyFaceDots from "./SmileyFaceDots";
import BetterSlider from "~/components/BetterSlider";
import { ratingColorGradient } from "~/util/util";
import { useLocation, useParams } from "react-router-dom";
import RatingResultUnified, { getAgeGroup } from "./results/RatingResultUnified";
import OptionsButton from "~/components/OptionsButton";
import { useTranslation } from "react-i18next";
import { GroupResult, RatingPostRequest } from "~/types";
import MatomoTracker from "~/util/matomo/MatomoTracker";
import LanguageSelector from "./LanguageSelector";
import LanguageSelectorV2 from "./New/LanguageSelectorV2";

const PartnerLogo = styled.div`
    position: absolute;
    text-align: right;
    right: 40px;

    p {
        left: 5px;
        bottom: 105px;
        width: 150px;
        font-size: 14px;
        color: black;
        margin: 0;
        color: #fff;
        opacity: 0.8;
    }

    img {
        left: 15px;
        bottom: 60px;
        width: 77px; 
        opacity: 1;
    }
`;

const SmileyFaceContainer = styled.div`
    position: absolute;
    left: 2px;
    margin-top: -10px;
    width: 215px;
    height: 215px;

    @media only screen and (max-width: 550px) {
        width: calc(100vw / 2 - 60px);
        height: calc(100vw / 2 - 60px);
    }
`;

export type SliderTouchedFunction = () => void;
export type SliderMovedFunction = (sliderValue: number) => void;
export type SubmitRatingFunction = (ratingValue: number | null) => void;
export type SliderResetFunction = () => void;

export const RATING_COLORS = ['#e63939', '#ff4d40', '#c36eff', '#538cff', '#53b7ff', '#1ed931'];
//export const RATING_COLORS = ['#E21E28', '#614496', '#1E4389', '#1AA7C2', '#81BC7A', '#308F4B'];

export enum SliderAnimationState {
    Initial = 'initial',
    SliderTransform = 'slider-transform',
    ResultsFadeIn = 'results-fade-in'
}

enum RatingState {
    Initial = 'initial',
    SliderTouched = 'slider-touched',
    SliderMoved = 'slider-moved',
    SliderReleased = 'slider-released'
}

interface SliderPanelProps {
    sliderActive: boolean;
    ratingValue: number | null;
    ratingId: number | null;
    submitRatingValue: (ratingValue: number) => void;
    setRatingId: (ratingId: number) => void;
    onAnimationComplete: () => void;
    firstRating: boolean;
}

interface SliderPanelState {
    ratingState: RatingState;
    sliderAnimationState: SliderAnimationState;
    ratingOpenTime: number | null;
    sliderTouchTime: number | null;
    referenceRating: number | null;
    referenceColor: string | null;
    loadingResults: { [groupString: string]: boolean };
    groupResults: { [groupString: string]: GroupResult };
    languageSelectorVariant: 'new' | 'old';
}

const SliderPanel: React.FC<SliderPanelProps> = (props) => {

    const {
        apiUrl,
        topicData,
        setGlassPanelColor,
        setGlassPanelBackdropFilter,
        profile, setProfile,
        preciseCoordinates,
        languageSelectorVariant,
        recordAnalyticsEvent
    } = useContext(AppContext)!;

    const { topicHash }: { topicHash: string } = useParams();
    const location = useLocation();

    const updateSmileyRef = useRef<(sliderValue: number) => void>();

    const { t, i18n } = useTranslation();
    
    const changeLanguageTrackedRef = useRef<boolean>(false);
    
    const trackLanguageChange = () => {
        if (changeLanguageTrackedRef.current) return;
        recordAnalyticsEvent(
            'language-selector-ab-test-2025-11',
            'change-language',
            topicData?.topic.id,
            topicData?.entity.id
        );
        changeLanguageTrackedRef.current = true;
    };

    const params: { topicHash: string } = useParams();

    const propsRef = useRef<SliderPanelProps>(props);
    useEffect(() => { propsRef.current = props; }, [props]);

    const animationCompleteCalled = useRef<boolean>(false);

    const [, api] = useSpring(() => ({
        color: '#6664',
        onChange: (values) => {
            setGlassPanelColor(values.value.color);
        }
    }));

    const [state, setState] = useState<SliderPanelState>({
        ratingState: RatingState.Initial,
        sliderAnimationState: SliderAnimationState.Initial,
        ratingOpenTime: null,
        sliderTouchTime: null,
        referenceRating: null,
        referenceColor: null,
        loadingResults: { majority: true, gender: true, age: true, ageGender: true },
        groupResults: {},
        languageSelectorVariant: languageSelectorVariant
    });

    const stateRef = useRef<SliderPanelState>(state);

    useEffect(() => {
        if (topicData) {
            stateRef.current = { ...stateRef.current, ratingOpenTime: Date.now() };
            setState(stateRef.current);
        }
    }, [topicData]);

    const loadInitialResults = async () => {

        if(!profile) return;

        const res = await axios.get(`${apiUrl}/topic/result/${topicHash}`, {
            params: {
                majority: true, gender: profile.gender, age: getAgeGroup(profile.birthYear)
            }
        });

        stateRef.current = {
            ...stateRef.current,
            loadingResults: { majority: false, gender: false, age: false, ageGender: false },
            groupResults: { ...stateRef.current.groupResults, ...res.data.groupResults }
        };
        setState(stateRef.current);
    };

    const loadExtraResults = async (gender: GenderData, birthYear: number | null) => {

        const requestStartedTimestamp = Date.now();

        const res = await axios.get(`${apiUrl}/topic/result/${topicHash}`, {
            params: {
                gender, age: birthYear ? getAgeGroup(birthYear) : undefined
            }
        });

        const stateUpdate = () => {

            const loadingResults = stateRef.current.loadingResults;

            for (const groupKey of Object.keys(res.data.groupResults)) {
                loadingResults[groupKey] = false;
            }

            stateRef.current = {
                ...stateRef.current,
                loadingResults,
                groupResults: { ...stateRef.current.groupResults, ...res.data.groupResults }
            };
            setState(stateRef.current);
        };

        stateUpdate();
    };

    const submitGender = async (e: React.ChangeEvent<HTMLSelectElement>) => {

        if(!profile) return;

        stateRef.current = {
            ...stateRef.current,
            loadingResults: {...stateRef.current.loadingResults,
                gender: true,
                ageGender: !!profile.birthYear
            },
        };
        setState(stateRef.current);

        const gender: GenderData = e.target.value as GenderData;
        await axios.put(`${apiUrl}/user`, {
            user: { gender },
            topicId: topicData?.topic.id,
            ratingValue: props.ratingValue
        });

        MatomoTracker.trackEvent({
            category: 'results', 
            action: 'submit-gender',
            documentTitle: topicData?.topic.name,
            href: `https://kazva.bg/${params.topicHash}`,
            customDimensions: [
                { id: 1, value: gender as string },
            ]
        });

        setProfile({ ...profile, gender });

        loadExtraResults(gender, profile.birthYear);
    };

    const submitBirthYear = async (e: React.ChangeEvent<HTMLSelectElement>) => {

        if(!profile) return;

        stateRef.current = {
            ...stateRef.current,
            loadingResults: {...stateRef.current.loadingResults,
                age: true,
                ageGender: !!profile.gender
            },
        };
        setState(stateRef.current);

        const birthYear: number = parseInt(e.target.value, 10);
        await axios.put(`${apiUrl}/user`, {
            user: { birthYear },
            topicId: topicData?.topic.id,
            ratingValue: props.ratingValue
        });

        MatomoTracker.trackEvent({
            category: 'results', 
            action: 'submit-birth-year',
            documentTitle: topicData?.topic.name,
            href: `https://kazva.bg/${params.topicHash}`,
            customDimensions: [
                { id: 2, value: birthYear.toString() },
            ]
        });

        setProfile({ ...profile, birthYear });

        loadExtraResults(profile.gender, birthYear);
    };

    const sliderTouched: SliderTouchedFunction = () => {
        //console.log('Slider touched', Date.now(), 'Pre-vote time: ', Date.now() - (state.voteOpenTime ?? 0));

        api.start({
            to: { color: '#fff8' },
            config: { duration: 1400 },
        });

        setGlassPanelBackdropFilter(40, 0, 1);

        if (state.ratingState === RatingState.Initial) {
            stateRef.current = {
                ...stateRef.current,
                ratingState: RatingState.SliderTouched,
                sliderTouchTime: Date.now()
            };
            setState(stateRef.current);
        }
    };

    const sliderMoved: SliderMovedFunction = (sliderValue: number) => {
        api.stop();

        if (updateSmileyRef.current)
            updateSmileyRef.current(sliderValue);

        let bgColor = '#808080';

        if (sliderValue) {
            bgColor = ratingColorGradient(RATING_COLORS, sliderValue);
        }

        setGlassPanelColor(bgColor + '4D');

        if (state.ratingState !== RatingState.SliderMoved) {
            stateRef.current = { ...stateRef.current, ratingState: RatingState.SliderMoved };
            setState(stateRef.current);
        }
    }

    const sliderReset: SliderResetFunction = () => {
        stateRef.current = { ...stateRef.current, ratingState: RatingState.Initial };
        setState(stateRef.current);
    }

    //const submitVoteRequestTimestampRef = useRef<number>(0);

    //Refresh Slider Value on first vote change
    useEffect(() => {
        sliderReleased(props.ratingValue);
    }, [props.firstRating]);
    
    const sliderReleased: SubmitRatingFunction = async (ratingValue) => {
        //console.log('Slider released', Date.now(), 'Time in voting: ', Date.now() - (state.sliderTouchTime ?? 0));

        if (ratingValue === null) {
            //Starts the glass panel animation too early
            //setGlassPanelBackdropFilter(40, 1, 1);
            api.start({ to: { color: '#6664' }, config: { duration: 340 }, });
            return;
        }

        stateRef.current = {
            ...stateRef.current,
            ratingState: RatingState.SliderReleased,
            sliderAnimationState: SliderAnimationState.SliderTransform,
        };
        setState(stateRef.current);

        const timeToPress = state.sliderTouchTime === null || state.ratingOpenTime === null ? 0
            : state.sliderTouchTime - state.ratingOpenTime;
        const timeWhileHolding = state.sliderTouchTime === null ? 0 : (Date.now() - state.sliderTouchTime);

        //submitVoteRequestTimestampRef.current = Date.now();

        const searchParams = new URLSearchParams(location.search);
        const sources = searchParams.getAll('s').join(',');

        if (!props.firstRating){

            const ratingPostRequest: RatingPostRequest = {
                ratingValue,
                origin: document.referrer,
                sources,
                preciseCoordinates,
                timeToPressMs: timeToPress,
                timeWhileHoldingMs: timeWhileHolding,
                initialQuestionId: topicData?.initQuestion.id,
                lang: i18n.language,
            };

            const res = await axios.post(`${apiUrl}/rating/${topicData?.topic.id}`, ratingPostRequest);
                
            MatomoTracker.trackEvent({
                category: 'rating', 
                action: 'submit-rating',
                value: ratingValue,
                documentTitle: topicData?.topic.name,
                href: `https://kazva.bg/${topicHash}`
            });

            props.setRatingId(parseInt(res.data.ratingId, 10));
            loadInitialResults();
            
            if (stateRef.current.sliderAnimationState === SliderAnimationState.ResultsFadeIn) {
                if (!animationCompleteCalled.current) {
                    animationCompleteCalled.current = true;
                    propsRef.current.onAnimationComplete();
                }
            }
            // at this point you should decide if you want to fade in QnA
            // it's either slider transform or results-fade-in
        }
        props.submitRatingValue(ratingValue);

        setTimeout(() => {
            if (props.firstRating) return;
            else {
                stateRef.current = { ...stateRef.current, sliderAnimationState: SliderAnimationState.ResultsFadeIn };
                setState(stateRef.current);
            }
        }, 1000);
    };

    const setReferenceRating = (referenceRating: number | null, referenceColor: string | null) => {
        stateRef.current = { ...stateRef.current, referenceRating, referenceColor };
        setState(stateRef.current);
    };

    return (
        <>
            {!(state.ratingState === RatingState.Initial ||
                state.ratingState === RatingState.SliderTouched ||
                state.ratingState === RatingState.SliderMoved
            ) ? null :
                !topicData?.entity.logoUrl ? null :
                    <PartnerLogo>
                        <p>{t('slider_saying_to')}</p>
                        <img src={topicData.entity.logoUrl} />
                    </PartnerLogo>
            }
            {state.ratingState !== RatingState.Initial && state.ratingState !== RatingState.SliderTouched ? null :
                languageSelectorVariant === 'new'
                    ? <LanguageSelectorV2
                        topic={topicData?.topic ?? null}
                        entity={topicData?.entity ?? null}
                        altLngs={Object.keys(topicData?.translations ?? {})}
                        onLanguageChange={trackLanguageChange}
                    />
                    : <LanguageSelector
                        topic={topicData?.topic ?? null}
                        entity={topicData?.entity ?? null}
                        altLngs={Object.keys(topicData?.translations ?? {})}
                        onLanguageChange={trackLanguageChange}
                    />
            }
            {state.ratingState !== RatingState.SliderMoved ? null :
                <SmileyFaceContainer>
                    <SmileyFaceDots
                        updateSmileyRef={(updateSmiley: (sliderValue: number) => void) => updateSmileyRef.current = updateSmiley}
                    />
                </SmileyFaceContainer>
            }
            <BetterSlider
                sliderActive={props.sliderActive}
                sliderTouched={sliderTouched}
                sliderMoved={sliderMoved}
                sliderReleased={sliderReleased}
                sliderReset={sliderReset}
                shrink={props.ratingValue ? true : false}
                referenceRating={state.referenceRating}
                referenceColor={state.referenceColor}
            />
            {state.ratingState !== RatingState.Initial ? null :
                <OptionsButton absolute />
            }
            {state.sliderAnimationState !== SliderAnimationState.ResultsFadeIn ? null :
                <RatingResultUnified
                    ratingValue={props.ratingValue ?? 50}
                    ratingId={props.ratingId}
                    onAnimationComplete={() => {
                        if (!animationCompleteCalled.current) {
                            animationCompleteCalled.current = true;
                            propsRef.current.onAnimationComplete();
                        }
                    }}
                    setReferenceRating={setReferenceRating}
                    sliderAnimationState={state.sliderAnimationState}
                    groupResults={state.groupResults}
                    submitGender={submitGender}
                    submitBirthYear={submitBirthYear}
                    loadingResults={state.loadingResults}
                />
            }
        </>
    );
}

export default SliderPanel;